/**
 * PaymentDelayWidget — Score délai de paiement agrégé (Phase 8).
 * Métrique tenant, non-sensible RGPD. Glissant 12 mois avec tendance vs 12 mois précédents.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, ArrowUp, ArrowDown, Minus, Loader2, Sparkles } from "lucide-react";

function TrendPill({ trend, delta }) {
  if (delta === null || trend === "stable") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-[11px] text-slate-400"
            data-testid="payment-delay-trend">
        <Minus className="h-3 w-3" /> Stable
      </span>
    );
  }
  const improving = trend === "improving";
  const cls = improving ? "badge-success" : "badge-error";
  const Icon = improving ? ArrowDown : ArrowUp;
  const abs = Math.abs(Math.round(delta));
  const label = improving ? `-${abs} j` : `+${abs} j`;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${cls}`}
          data-testid="payment-delay-trend">
      <Icon className="h-3 w-3" /> {label} vs période préc.
    </span>
  );
}

export default function PaymentDelayWidget() {
  const { state } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/dashboard/payment-delay-score")
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // Bug #2 fix (14/09/2026) — refetch quand company change dans le même onglet.
  }, [state?.company?.id]);

  const cur = data?.current || {};
  const hasData = cur.count > 0;

  return (
    <Card className="border-slate-800 bg-slate-900/40" data-testid="widget-payment-delay">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-200">
            <Clock className="h-4 w-4 text-[#3E7BB5]" /> Délai de paiement clients
          </h3>
          {hasData && data?.trend && (
            <TrendPill trend={data.trend} delta={data.delta_days} />
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          </div>
        ) : !hasData ? (
          <div className="flex flex-col items-center justify-center gap-2 py-6 text-center text-xs text-slate-500"
               data-testid="payment-delay-empty">
            <Clock className="h-6 w-6 text-slate-700" />
            <span>Pas encore assez de factures payées pour calculer une tendance.</span>
          </div>
        ) : (
          <>
            <div className="rounded border border-slate-800 bg-slate-950/30 p-4 text-center">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">
                Délai moyen sur 12 mois
              </div>
              <div className="mt-1 text-4xl font-bold text-slate-100 tabular-nums"
                   data-testid="payment-delay-avg">
                {cur.avg_delay_days > 0 ? "+" : ""}{cur.avg_delay_days}
                <span className="ml-2 text-lg font-normal text-slate-400">jours</span>
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                Sur {cur.count} factures payées · positif = retard, négatif = avance
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <div className="rounded border border-slate-800 bg-slate-950/30 p-2.5" data-testid="payment-delay-median">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Médiane</div>
                <div className="text-lg font-semibold text-slate-100 tabular-nums">
                  {cur.median_delay_days}<span className="ml-0.5 text-xs text-slate-400">j</span>
                </div>
              </div>
              <div className="rounded border border-slate-800 bg-slate-950/30 p-2.5" data-testid="payment-delay-ontime">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">À l&apos;heure</div>
                <div className="text-lg font-semibold text-[#B8C784] tabular-nums">
                  {cur.on_time_pct}<span className="ml-0.5 text-xs text-slate-400">%</span>
                </div>
              </div>
              <div className="rounded border border-slate-800 bg-slate-950/30 p-2.5" data-testid="payment-delay-over30">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">&gt;30 j retard</div>
                <div className="text-lg font-semibold text-[#E9A98F] tabular-nums">
                  {cur.over_30d_late_pct}<span className="ml-0.5 text-xs text-slate-400">%</span>
                </div>
              </div>
            </div>
            {data?.cta?.show && (
              <Link
                to={data.cta.target || "/settings/invoicing#reminders"}
                onClick={() => {
                  api.post("/dashboard/cta/track-click", { kind: data.cta.kind })
                    .catch((err) => console.warn("[CTA] track-click failed:", err?.message || err));
                }}
                className="mt-3 flex items-center justify-between gap-2 rounded border border-[#C4553B]/40 bg-[#C4553B]/10 p-2.5 text-xs text-slate-200 transition-colors hover:bg-[#C4553B]/15"
                data-testid="payment-delay-cta-reminders"
              >
                <span className="flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5 text-[#C4553B]" />
                  <span>Vos clients tardent à payer ? Activez les relances automatiques par email.</span>
                </span>
                <span className="whitespace-nowrap font-medium text-[#E19A85]">Activer →</span>
              </Link>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
